<script>
import { homeComputed } from '@state/helpers';
import { breakpointsMixin } from '@mixins/breakpointsMixin';
import { counterMixin } from '@mixins/counter-mixin';

export default {
  components: {},
  mixins: [breakpointsMixin, counterMixin],
  props: {
    scrollState: {
      type: Object,
      required: false,
      default: () => {},
    },
    workout: {
      type: Object,
      required: true,
      default: () => {},
    },
    equipments: {
      type: String,
      required: true,
      default: '',
    },
  },
  data() {
    return {};
  },
  mounted() {},
  computed: {
    ...homeComputed,
    coverPhoto() {
      return this.workout &&
        this.workout.mediaAssets &&
        this.workout.mediaAssets.coverPhoto &&
        this.workout.mediaAssets.coverPhoto.url
        ? this.workout.mediaAssets.coverPhoto.url
        : require('@assets/images/placeholders/hero-workout.jpg');
    },
    timeToComplete() {
      if (this.workout && this.workout.timeToComplete && parseInt(this.workout.timeToComplete) > 0) {
        const unit = this.timeUnit(parseInt(this.workout.timeToComplete)).substring(0, 3);
        const time = this.abbreviatedTime(parseInt(this.workout.timeToComplete));
        return `${time} ${unit}`;
      } else {
        return '';
      }
    },
    isWorkoutStarted() {
      return this.workout && this.workout.clientInput && this.workout.clientInput.startedAt;
    },
  },
};
</script>

<template>
  <div :class="['workout-hero', scrollState]">
    <div id="hero-container" class="hero-container" :style="{ 'background-image': `url(${coverPhoto})` }">
      <span @click="$router.push({ name: 'home' })">
        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg" v-bind:class="'icon-close'" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><g filter="url(#filter0_b)"><circle cx="16" cy="16" r="16" fill="#9F9F9F" fill-opacity=".4"/></g><path d="M20 12l-8 8m0-8l8 8" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><defs><filter id="filter0_b" x="-4" y="-4" width="40" height="40" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB"><feFlood flood-opacity="0" result="BackgroundImageFix"/><feGaussianBlur in="BackgroundImage" stdDeviation="2"/><feComposite in2="SourceAlpha" operator="in" result="effect1_backgroundBlur"/><feBlend in="SourceGraphic" in2="effect1_backgroundBlur" result="shape"/></filter></defs></svg>
      </span>
      <div class="hero-content">
        <h1>{{ workout.label }}</h1>
        <div class="workout-meta">
          <h3>
            <span>{{ timeToComplete }}</span>
            <span v-if="timeToComplete && equipments">•</span>
            <span>{{ equipments }}</span>
          </h3>
        </div>
      </div>
      <h5 class="collapsed-title">Workout Overview</h5>
      <BaseButton @click.stop="$emit('start-workout')" :class="['button btn-primary large btn-start-workout', scrollState]">
        <BaseIcon class="icon-caret-right" name="caret-right" />
        <span> {{ isWorkoutStarted ? 'Resume' : 'Start' }} <span v-if="isDesktopView">Workout</span> </span>
      </BaseButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.workout-hero {
  transition: position 0.5s ease-in-out;

  .hero-container {
    background-position: center;
    background-repeat: no-repeat;
    background-size: cover;
    border-bottom-left-radius: 2.4rem;
    border-bottom-right-radius: 2.4rem;
    height: 68vh;
    max-height: 68%;
    min-height: 15%;
    padding: 1.6rem 5vw;
    position: fixed;
    transition: background-position 0.5s ease-in-out;
    width: 100%;
    will-change: height;

    &::before {
      border-bottom-left-radius: 2.4rem;
      border-bottom-right-radius: 2.4rem;
      bottom: 0;
      content: '';
      left: 0;
      opacity: 0.7;
      position: absolute;
      right: 0;
      top: 0;

      @include media('xl', null) {
        background: $color-black-alt;
      }
    }

    .icon-close {
      position: relative;
      top: 5.5vh;
      transition: top 0.5s ease-in-out;
      z-index: 11;

      @include media('xxl', null) {
        top: 10vh;
      }

      &:hover {
        cursor: pointer;
      }
    }
  }

  .hero-content {
    bottom: 5rem;
    position: absolute;

    h1 {
      @include calc-font-size(2);

      color: white;
      font-family: $base-font-family;
      font-weight: bold;
      margin-bottom: 0;
      text-transform: capitalize;

      @include media('xl', null) {
        @include calc-font-size(4);
      }
    }

    .workout-meta {
      @include media('lg', null) {
        margin-bottom: 6.2rem;
      }
    }
  }

  .collapsed-title {
    bottom: 4rem;
    color: white;
    display: none;
    font-size: 1.3rem;
    font-weight: 600;
    letter-spacing: -0.41px;
    line-height: 2.2rem;
    position: relative;
    text-align: center;
  }

  .btn-start-workout {
    align-items: center;
    bottom: -3rem;
    display: flex;
    justify-content: center;
    margin: 0 auto;
    margin-left: 30vw;
    margin-right: 30vw;
    min-width: 32vw;
    padding: unset;
    padding: 1vw 8vw;
    position: absolute;
    transition: display 0.5s ease-in-out;
    width: fit-content;
    @include media('xxl', null) {
      bottom: 22rem;
      margin-left: 5vw;
      margin-right: unset;
    }

    .icon-caret-right {
      height: 2.7rem;
      margin-right: 7px;
      width: 2.7rem;
    }

    span {
      @include calc-font-size(1.6);
    }
  }

  .workout-meta h3 {
    @include calc-font-size(1.5);

    color: white;
    font-family: $secondary-font-family;
    font-weight: 300;
    line-height: 1.3;
    mix-blend-mode: normal;

    @include media('lg', null) {
      @include calc-font-size(2);

      line-height: 3.7rem;
    }

    span {
      margin-right: 2rem;
    }
  }

  &.minimize {
    position: fixed;
    width: 100%;
    z-index: 1;

    .hero-container {
      .icon-close {
        top: 4vh;

        @include media('xxl', null) {
          top: 10vh;
        }
      }
    }

    .collapsed-title {
      bottom: 1rem;
      display: block;
      margin-left: auto;
      position: absolute;
      white-space: nowrap;
      width: 90vw;
    }

    .btn-start-workout {
      background-color: transparent;
      bottom: 0;
      left: 65vw;
      margin: 0;
      text-transform: uppercase;

      .icon-caret-right {
        height: 2rem;
        margin-right: 0;
        width: 2rem;
      }

      span {
        @include calc-font-size(1.5);
      }
    }

    .hero-content {
      display: none;
    }
  }
}
</style>
